import React, { useEffect, useState } from "react";
import { Share2, Play } from "lucide-react";
import { useHoverLens } from "../../../ProductDetails/hooks/useHoverLens";
import { ProductGalleryItem } from "../../../../../../services/helpers/ProductImageHelper";
import mediaUtility from "../../../../../../services/utility/MediaUtility";
import "./styles/DesktopGallery.css";

interface DesktopGalleryProps {
  /** Images + videos in payload order (backend order = display order). */
  items: ProductGalleryItem[];
  currentIndex: number;
  carouselRef: React.RefObject<HTMLDivElement>;
  activeImgRef: React.RefObject<HTMLImageElement | null>;
  onScroll: React.UIEventHandler<HTMLDivElement>;
  onWheel: React.WheelEventHandler<HTMLDivElement>;
  onGoToImage: (idx: number) => void;
  onOpenViewer: (idx: number) => void;
  onShare: () => void;
}

const DesktopGallery: React.FC<DesktopGalleryProps> = ({
  items,
  currentIndex,
  carouselRef,
  activeImgRef,
  onScroll,
  onWheel,
  onGoToImage,
  onOpenViewer,
  onShare,
}) => {
  const [viewportH, setViewportH] = useState(window.innerHeight);
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const current = items[currentIndex];

  const LENS_SIZE = 150;
  const { showLens, lensPos, lensBg, onLensEnter, onLensLeave, onLensMove } =
    useHoverLens(activeImgRef as React.RefObject<HTMLImageElement>, LENS_SIZE, 2, {
      width: Math.min(560, window.innerWidth * 0.52),
      height: viewportH - 120,
    });

  const handleSingleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.currentTarget as HTMLImageElement).style.display = "none";
  };

  return (
    <div className="dgal-root">
      <div
        className="dgal-main"
        onMouseEnter={onLensEnter}
        onMouseLeave={onLensLeave}
        onMouseMove={onLensMove}
      >
        <div
          className="dgal-carousel"
          ref={carouselRef}
          onScroll={onScroll}
          onWheel={onWheel}
        >
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`dgal-slide${idx === currentIndex ? " dgal-slide--active" : ""}`}
            >
              {item.kind === "video" ? (
                /* Inline preview: muted looping autoplay. Sound + controls
                   live in the fullscreen viewer (click). */
                <video
                  className="dgal-img"
                  src={item.url}
                  muted
                  loop
                  autoPlay
                  playsInline
                  preload="metadata"
                  onClick={() => onOpenViewer(idx)}
                />
              ) : (
                <img
                  ref={idx === currentIndex ? (activeImgRef as any) : null}
                  src={item.url}
                  alt={`Product ${idx + 1}`}
                  className="dgal-img"
                  onError={handleSingleImageError}
                  onClick={() => onOpenViewer(idx)}
                  draggable={false}
                />
              )}
            </div>
          ))}
        </div>

        {/* Hover zoom lens applies to images only. */}
        {showLens && current?.kind === "image" && (
          <div
            className="dgal-lens"
            style={{
              left: lensPos.x,
              top: lensPos.y,
              width: LENS_SIZE,
              height: LENS_SIZE,
              backgroundImage: mediaUtility.cssUrl(current.url),
              backgroundSize: lensBg.size,
              backgroundPosition: lensBg.position,
            }}
          />
        )}

        <div className="dgal-overlay-btns">
          <button
            className="dgal-overlay-btn"
            onClick={(e) => { e.stopPropagation(); onShare(); }}
            aria-label="Share"
          >
            <Share2 size={16} />
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="dgal-dots">
        {items.map((_, i) => (
          <button
            key={i}
            className={`dgal-dot${i === currentIndex ? " dgal-dot--active" : ""}`}
            onClick={(e) => { e.stopPropagation(); onGoToImage(i); }}
            aria-label={`Media ${i + 1}`}
          />
        ))}
      </div>

      {/* Thumbnails */}
      <div className="dgal-thumbs">
        {items.map((item, i) => (
          <div
            key={i}
            className={`dgal-thumb${i === currentIndex ? " dgal-thumb--active" : ""}`}
            onClick={(e) => { e.stopPropagation(); onGoToImage(i); }}
            role="button"
            aria-label={`Thumbnail ${i + 1}`}
          >
            {item.kind === "video" ? (
              <>
                <video src={item.url} muted playsInline preload="metadata" />
                <span className="dgal-thumb-play" aria-hidden>
                  <Play size={12} fill="currentColor" />
                </span>
              </>
            ) : (
              <img src={item.url} alt={`Thumb ${i + 1}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DesktopGallery;
